import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { User, Dog, BookOpen, Crown, Library, Pen } from 'lucide-react';
import UsageCounter from '@/components/UsageCounter';
import SubscriptionBadge from '@/components/SubscriptionBadge';
import { toast } from 'sonner';
import GuestModeBanner from '@/components/GuestModeBanner';
import { getLevel } from '@/utils/readingLevels';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, nightMode, refreshUser } = useContext(AuthContext);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingUser, setRefreshingUser] = useState(false);
  const isGuest = user?.id === 'guest-user';

  useEffect(() => {
    if (token) {
      // Parallelize initial data fetching for faster load
      const loadInitialData = async () => {
        const promises = [fetchChildren()];
        
        // Only refresh user for non-guests
        if (token !== 'guest-token') {
          setRefreshingUser(true);
          promises.push(refreshUser().finally(() => setRefreshingUser(false)));
        }
        
        await Promise.all(promises);
      };
      
      loadInitialData();
    }

    const handleFocus = async () => {
      if (token && token !== 'guest-token') {
        setRefreshingUser(true);
        try {
          await refreshUser();
        } finally {
          setRefreshingUser(false);
        }
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [token]);

  const fetchChildren = async () => {
    try {
      // Use metadata endpoint for faster loading (returns thumbnails instead of full images)
      const response = await axios.get(`${API}/children/metadata`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        params: { limit: 8 }
      });
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        // It's possible for new users to not have children, which might result in a 404.
        // We'll treat it as an empty list and not show an error.
        if (error.response?.status !== 404) {
          toast.error('Failed to load profiles');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    document.cookie = 'session_token=; Max-Age=0; path=/;';
    window.location.href = '/';
  };

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {isGuest && <GuestModeBanner />}

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {isGuest && (
          <div className="story-card mb-4 sm:mb-6 text-center p-4" style={{ background: 'var(--accent-4)', color: 'var(--text-primary)' }}>
            <p className="text-base sm:text-lg font-semibold mb-3">You're in Guest Mode. Sign up to create your own stories!</p>
            <Button
              data-testid="guest-signup-btn"
              onClick={() => {
                logout();
                window.location.href = '/';
              }}
              className="pill-button"
              style={{ background: 'var(--accent-2)', color: 'white' }}
            >
              Sign Up Now
            </Button>
          </div>
        )}

        <div className="text-center mb-4 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-bold mb-2" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Welcome, {user?.display_name}!
          </h2>
          <p className="text-base sm:text-lg" style={{ color: 'var(--text-secondary)' }}>What would you like to do today?</p>
          
          {/* Subscription & Usage Info */}
          {!isGuest && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 mt-3">
              {refreshingUser ? (
                <div className="px-3 py-1 text-sm text-gray-500">Updating subscription...</div>
              ) : (
                <SubscriptionBadge tier={['starter', 'folio', 'guest'].includes(user?.subscription_tier) ? 'free' : (user?.subscription_tier || 'free')} />
              )}
              <Button
                onClick={() => navigate('/subscription')}
                variant="outline"
                size="sm"
                className="font-sketch text-sm"
                style={{
                  background: nightMode ? 'var(--bg-secondary)' : 'white',
                  color: nightMode ? 'var(--text-primary)' : 'inherit',
                  borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                }}
              >
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Upgrade Plan
              </Button>
            </div>
          )}
        </div>

        {/* Usage Counter */}
        {!isGuest && (
          <div className="max-w-md mx-auto mb-4 sm:mb-6">
            <UsageCounter />
          </div>
        )}

        {/* Quick Actions - Mobile Optimized */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            data-testid="quick-action-children"
            onClick={() => navigate('/children')}
            className="story-card text-center cursor-pointer p-4 sm:p-6 hover:scale-105 transition-transform"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-1)' }}>
              <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h3 className="text-sm sm:text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Manage Children</h3>
            <p className="text-xs hidden sm:block" style={{ color: 'var(--text-secondary)' }}>Add or edit profiles</p>
          </button>

          <button
            data-testid="quick-action-avatars"
            onClick={() => navigate('/avatars')}
            className="story-card text-center cursor-pointer p-4 sm:p-6 hover:scale-105 transition-transform"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-5)' }}>
              <Dog className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h3 className="text-sm sm:text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Build Characters</h3>
            <p className="text-xs hidden sm:block" style={{ color: 'var(--text-secondary)' }}>Create avatars</p>
          </button>

          <button
            data-testid="quick-action-generate"
            onClick={() => navigate('/generate')}
            className="story-card text-center cursor-pointer p-4 sm:p-6 hover:scale-105 transition-transform"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-3)' }}>
              <Pen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h3 className="text-sm sm:text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Generate Story</h3>
            <p className="text-xs hidden sm:block" style={{ color: 'var(--text-secondary)' }}>Create new story</p>
          </button>

          <button
            data-testid="quick-action-library"
            onClick={() => navigate('/library')}
            className="story-card text-center cursor-pointer p-4 sm:p-6 hover:scale-105 transition-transform"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-2)' }}>
              <Library className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h3 className="text-sm sm:text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Story Library</h3>
            <p className="text-xs hidden sm:block" style={{ color: 'var(--text-secondary)' }}>View all stories</p>
          </button>

          <button
            data-testid="quick-action-folio"
            onClick={() => navigate('/folio')}
            className="story-card text-center cursor-pointer p-4 sm:p-6 hover:scale-105 transition-transform"
          >
            <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-3 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-4)' }}>
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
            </div>
            <h3 className="text-sm sm:text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Folio Series</h3>
            <p className="text-xs hidden sm:block" style={{ color: 'var(--text-secondary)' }}>Curated collections</p>
          </button>
        </div>

        {/* Recent Stories */}
        {children.length > 0 && (
          <div>
            <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4" style={{ color: 'var(--text-primary)' }}>Learning History and Scoreboard</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {children.slice(0, 8).map((child) => (
                <div
                  key={child.id}
                  className="story-card" data-testid={`child-profile-${child.id}`}
                  onClick={() => navigate(`/children/${child.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {child.profile_image && (
                    <div className="mb-2 sm:mb-3">
                      <img
                        src={`data:image/png;base64,${child.profile_image}`}
                        alt={child.name}
                        className="w-full h-24 sm:h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  <h4 className="text-sm sm:text-base font-bold text-center" style={{ color: 'var(--text-primary)' }}>
                    {child.name}
                  </h4>
                  <p className="text-xs text-center mt-1 capitalize" style={{ color: 'var(--text-secondary)' }}>
                    {getLevel(child.reading_level)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
